import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { CatalogComponent, Product, SelectionGroup } from "../types/catalog";
import { ProductModalProduct, ProductModalSelectionGroup } from "../types/product_modal";
import { QuantityInput } from "./quantity_input";
import { classNames } from "../../common_util";
import { ReactComponent as LoadingSpinner } from "images/animated/loading_spinner.svg?react";

import { SelectionGroupFormControl } from "./selection_group_form_control";
import { dynamicBackgroundProperties } from "src/image_set";
import { SelectBreadcrumb, useNavigationState } from "src/storefront/types/navigation";
import { Breadcrumb, Breadcrumbs } from "src/storefront/components/breadcrumbs";
import { OutOfStockResolutionPreference } from "src/storefront/types/out_of_stock_resolution_preference";
import { ProductModalSubstitutionsInfo } from "src/storefront/components/product_modal_substitutions_info";
import { StreamlineIcon } from "src/components/icons";
import addIconUrl from "images/icons/add.svg";
import arrowDown1IconUrl from "images/icons/arrow-down-1.svg";
import arrowLeftIconUrl from "images/icons/arrow-left.svg";
import arrowRightIconUrl from "images/icons/arrow-right.svg";
import bin2IconUrl from "images/icons/bin-2.svg";
import checkIconUrl from "images/icons/check-1.svg";
import chevronRightIconUrl from "images/icons/chevron-right.svg";
import deleteIconUrl from "images/icons/delete.svg";
import moveExpandVerticalIconUrl from "images/icons/move-expand-vertical.svg";
import removeIconUrl from "images/icons/remove.svg";

const MAX_ITEM_QUANTITY = 99;

const ProductModalCard = ({
  productModalProduct,
  label,
  confirmProductSelections,
  modalBack,
  closeModal,
  addCustomisationModal,
  isVisible,
  isEdit,
  isNested,
  remainingQuantityAllowed,
  disabledButtons,
} : {
  productModalProduct: ProductModalProduct;
  label?: string;
  confirmProductSelections: (productModalProduct: ProductModalProduct) => Promise<void>;
  modalBack: () => void;
  closeModal: () => void;
  addCustomisationModal: (productModalProduct: ProductModalProduct, label: string, onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>, onCancel: (reason: string) => Promise<void>, remainingQuantityAllowed?: number) => void;
  isVisible: boolean;
  isEdit: boolean;
  isNested: boolean;
  remainingQuantityAllowed?: number;
  disabledButtons: boolean;
}): React.JSX.Element => {
  const config = useStorefrontConfig();
  const { catalog, isOrderable } = useStorefrontHelper();

  const product = productModalProduct.product;
  const formatter = Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
  const [isConfirming, setConfirming] = useState<boolean>(false);
  const [isConfirmDelay, setConfirmDelay] = useState<boolean>(false);
  const isTimedOut = !catalog.isInFulfillableTimeRange(config, product);

  const [modalState, setModalState] = useState<ProductModalProduct>(productModalProduct);

  const onChangeMealPreferences = useCallback((newMealPreferences: string) => {
    setModalState(modalState.copyWith({ mealPreferences: newMealPreferences }));
  }, [modalState]);

  const onSetQuantity = useCallback((newQuantity: number) => {
    setModalState(modalState.copyWith({ quantity: Math.max(1, newQuantity) }));
  }, [modalState]);

  const onChangeSelectionGroupSelections = useCallback((selectionGroupKey: string, newSelectionProducts: ProductModalProduct[]) => {
    if (product.kind == "leaf") return;

    const relevantSelectionGroup = product.selectionGroups.find(sg => sg.key == selectionGroupKey);
    if (!relevantSelectionGroup) return;

    let updated = false;

    let newSelectionGroups = modalState.selectionGroups.map(sg => {
      if (sg.selectionGroup.key == selectionGroupKey) {
        updated = true;

        return sg.copyWith({
          productSelections: relevantSelectionGroup.products.reduce((accl: ProductModalProduct[], potentialProduct: Product) => {
            const newProductModalProducts = newSelectionProducts.filter(nsp => {
              return nsp.product.key == potentialProduct.key;
            });

            if (newProductModalProducts.length > 0)
              return accl.concat(newProductModalProducts);

            return accl;
          }, []),
        });
      } else {
        return sg;
      }
    });

    if (!updated) {
      const newCartItems = relevantSelectionGroup.products.reduce((accl: ProductModalProduct[], potentialProduct: Product) => {
        const newProductModalProducts = newSelectionProducts.filter(nsp => {
          return nsp.product.key == potentialProduct.key;
        });

        if (newProductModalProducts)
          return accl.concat(newProductModalProducts);

        return accl;
      }, []);

      const newSelectionGroup = new ProductModalSelectionGroup(relevantSelectionGroup, newCartItems);
      newSelectionGroups = newSelectionGroups.concat(newSelectionGroup);
    }

    const newModalState = modalState.copyWith({
      selectionGroups: newSelectionGroups
    });

    setModalState(newModalState);
  }, [modalState, product]);

  const onConfirmSubmit = useCallback(async () => {
    setConfirmDelay(false);
    setConfirming(true);
    window.setTimeout(() => {
      setConfirmDelay(true);
    }, 200);
    await confirmProductSelections(modalState);
    setConfirming(false);
  }, [confirmProductSelections, modalState]);

  const classes = classNames({ "card": true, "is-completely-hidden": !isVisible });
  const badge = label && <div className="badge is-flat is-elevated-middle">
    {label}
  </div>;

  const loadingSpinner = () => {
    if (!isConfirming || !isConfirmDelay) return;

    return (
      <span className="icon custom-icon">
        <LoadingSpinner/>
      </span>
    )
  }

  const buttonLabel = () => {
    if (isConfirmDelay && (isNested || isEdit))
      return `Saving...`;
    else if (isNested || isEdit)
      return `Save choices`
    else if (isConfirmDelay)
      return `Adding to cart...`;
    else if (modalState.quantity > 1)
      return `Add ${modalState.quantity} to cart`;
    else if (config.presentationStatus == 'open_for_business' && isTimedOut)
      return `Currently unavailable`;
    else if (config.presentationStatus == 'open_for_business' && isOrderable(product))
      return `Add to cart`;
    else if (config.presentationStatus == 'open_for_business')
      return `Out of stock`
    else if (config.presentationStatus == 'no_store_service')
      return `Address not serviced`
    else if (config.presentationStatus == 'no_customer_pickup')
      return `Does not allow pickups`
    else
      return `Currently closed`;
  }

  const subtotalLabel = () => {
    const prefix = isNested ? `+ ` : ``;
    const sub = modalState.subtotal({ excludeSelf: isNested });

    if (modalState.subtotal() > 0 && !(isNested && sub === 0)) {
      return <span>{prefix}{formatter.format(sub / 100.0)}</span>;
    } else {
      return ``;
    }
  }

  const buttonClasses = classNames({
    "button": true,
    "is-primary": true,
    "is-full-width": true,
    "has-space-between": modalState.subtotal() > 0,
    "justify-content-center": modalState.subtotal() == 0,
    "is-loading": isConfirming,
  });

  const arrowClasses = classNames({
    "is-hidden-above-desktop": !isNested,
  });

  const exitClasses = classNames({
    "is-visible-above-desktop": true,
  });

  const maxQuantity = Math.min(
    product.maxQuantity ?? Number.POSITIVE_INFINITY,
    remainingQuantityAllowed ?? Number.POSITIVE_INFINITY,
    MAX_ITEM_QUANTITY,
  );

  const quantityInput = maxQuantity > 1 ?
      <ProductQuantity
        quantity={modalState.quantity}
        setQuantity={onSetQuantity}
        maxQuantity={maxQuantity}
        disabledButtons={disabledButtons}
      /> : null;

  const updateOutOfStockResolutionPreference = useCallback((newPreference: OutOfStockResolutionPreference) => {
    setModalState(modalState.copyWith({ outOfStockResolutionPreference: newPreference }));
  }, [modalState]);

  const substitutionInfo = useMemo(() => {
    if (!config.showSubstitutionsDisclaimer || isNested) return null;

    return <ProductModalSubstitutionsInfo
      allowSubstitutionPreferences={config.allowSubstitutionPreferences}
      outOfStockResolutionPreference={modalState.outOfStockResolutionPreference}
      onPreferenceSave={updateOutOfStockResolutionPreference}
    />
  }, [config.allowSubstitutionPreferences, config.showSubstitutionsDisclaimer, isNested, modalState.outOfStockResolutionPreference, updateOutOfStockResolutionPreference])

  const mealPreferencesInput = config.allowsMealPreferences && !isNested ?
    <ProductMealPreferences
      product={product}
      mealPreferences={modalState.mealPreferences}
      setMealPreferences={onChangeMealPreferences}
    /> : null;

  return (
    <div className={classes}>
      <div className="controls">
        <div className={arrowClasses}>
          <a className="button is-icon-only is-raised" onClick={modalBack} data-testid="modal-back">
            <StreamlineIcon href={arrowLeftIconUrl} />
          </a>
        </div>

        {badge}

        <div className={exitClasses}>
          <a className="button is-icon-only is-raised" onClick={closeModal}>
            <StreamlineIcon href={removeIconUrl} />
          </a>
        </div>
      </div>

      <ProductDetails product={product} showBreadcrumbs={!isNested}/>

      <ProductSelectionGroups
        product={product}
        modalState={modalState}
        setSelectionGroupSelections={onChangeSelectionGroupSelections}
        addCustomisationModal={addCustomisationModal}
        disabledButtons={disabledButtons}
      />

      {mealPreferencesInput}

      {quantityInput}

      {substitutionInfo}

      <div className="is-stuck-bottom">
        <section className="has-gradient">
          <div className="container">
            <button
              onClick={onConfirmSubmit}
              type="button"
              className={buttonClasses}
              disabled={!modalState.isValid() || isConfirming || disabledButtons}
              data-cy={"add-to-cart-button"}
              data-testid="add-to-cart-button">

              <span className="is-flexbox is-flex-gap-xs" data-testid="product-modal-confirm">
                {loadingSpinner()}
                {buttonLabel()}
              </span>

              {subtotalLabel()}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

const ChoiceLabel = ({
  option,
  selectionGroup,
  showChevron,
  onClick,
  disabledButtons,
  children,
}: {
  option: Product;
  selectionGroup: SelectionGroup;
  showChevron: boolean;
  onClick: () => void;
  disabledButtons: boolean;
  children?: React.ReactNode;
}): React.JSX.Element => {
  const formatter = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  });

  const noop = () => {
    // Ignore this action, using as cannot have empty arrow functions
    // and need to return one for the linter to be happy.
  };

  const formatPrice = (price: number) => formatter.format(price / 100.0);
  const priceDisplay = option.price ? <span className="additional-cost">{formatPrice(option.price)}</span> : <></>;
  const { isOrderable } = useStorefrontHelper();

  const outOfStockBadge = <>
    <div className="badge is-strong is-large is-visible-above-desktop"><StreamlineIcon href={deleteIconUrl} /><span>Out of stock</span></div>
    <div className="badge is-strong is-hidden-above-desktop"><span>Out</span></div>
  </>

  const keyVector = encodeURIComponent(`${selectionGroup.key}-${option.key}`);

  const chevron = showChevron ? <StreamlineIcon href={chevronRightIconUrl} /> : null;

  return (
    <label htmlFor={keyVector} onClick={disabledButtons ? noop : onClick} data-testid="add-with-customise">
      <span className="label-text">{option.title}</span>
      {isOrderable(option) ? priceDisplay : outOfStockBadge}
      {children}
      {chevron}
    </label>
  );
}

const selectionQuantityExplainer = (minSelections: number, maxSelections?: number): string | undefined => {
  if (minSelections <= 0) {
    if (!maxSelections)
      return;
    else if (maxSelections > 0)
      return `Choose up to ${maxSelections}`;
    else
      throw "Impossible min/max selections combination";
  } else {
    if (!maxSelections)
      return `Choose at least ${minSelections}`;
     else if (maxSelections === minSelections)
      return `Choose ${minSelections}`;
    else if (maxSelections > minSelections)
      return `Choose ${minSelections} - ${maxSelections}`
    else
      throw "Impossible min/max selections combination";
  }
}

const priceAdjustmentLabel = (priceInCents: number) => {
  if (priceInCents === 0) return "";

  const priceFormatter = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
  const formattedPrice = priceFormatter.format(Math.abs(priceInCents) / 100);

  const plusOrMinus = priceInCents > 0 ? "+" : "-";

  return ` (${plusOrMinus} ${formattedPrice})`;
}

const ProductCustomisation = ({
  productModalSelectionGroup,
  setCustomisationSelection,
  addCustomisationModal,
  disabledButtons,
  includeThumbnailSpacing,
}: {
  productModalSelectionGroup: ProductModalSelectionGroup;
  setCustomisationSelection: (products: ProductModalProduct[]) => void;
  addCustomisationModal: (productModalProduct: ProductModalProduct, label: string, onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>, onCancel: (reason: string) => Promise<void>, remainingQuantityAllowed?: number) => void;
  disabledButtons: boolean;
  includeThumbnailSpacing: boolean;
}): React.JSX.Element => {
  const selectionGroup = productModalSelectionGroup.selectionGroup;
  const customisationSelections = productModalSelectionGroup.productSelections;

  const quantityHelperText = selectionQuantityExplainer(selectionGroup.minSelections, selectionGroup.maxSelections);

  const currentTotalSelections = customisationSelections.reduce((accl, cs) => accl + cs.quantity, 0);

  const maxReachedBadge = () : React.JSX.Element => {
    if (productModalSelectionGroup.reachedMaxThreshold()) {
      return (
        <div className="badge is-success space-ml-xs">
          <StreamlineIcon href={checkIconUrl} />
          Max reached
        </div>
      );
    } else {
      return (<></>);
    }
  }

  const minReachedBadge = () : React.JSX.Element => {
    if (
      productModalSelectionGroup.selectionGroup.minSelections > 0
      && productModalSelectionGroup.reachedMinThreshold()
      && !productModalSelectionGroup.reachedMaxThreshold()
    ) {
      return (
        <div className="badge is-success space-ml-xs">
          <StreamlineIcon href={checkIconUrl} />
          Min reached
        </div>
      );
    } else {
      return (<></>);
    }
  }

  const requiredBadge = () : React.JSX.Element => {
    if (!productModalSelectionGroup.reachedMinThreshold()) {
      return (
          <div className="badge is-strong space-ml-xs">Required</div>
      );
    } else {
      return (<></>);
    }
  }

  const classes = classNames({
    "divider": true,
    "has-content": true,
    "flex-wrap": true,
    "is-strong": !productModalSelectionGroup.isValid(),
  });

  const updateCustomisationSelections = useCallback((productModalProduct: ProductModalProduct) => {
    const existing = (opt: Product) => customisationSelections.find(pmp => pmp.product.key == opt.key);

    if (productModalProduct.quantity <= 0) {
      const id_target = customisationSelections.find(pmp => pmp.idTarget === productModalProduct.idTarget);
      const key_target = existing(productModalProduct.product);

      if (id_target) {
        return customisationSelections.filter(pmp => pmp.idTarget !== id_target.idTarget);
      } else if (key_target) {
        return customisationSelections.filter(pmp => pmp.idTarget !== key_target.idTarget);
      }
      return customisationSelections; // Implicitly already zero
    } else if (productModalProduct.product.kind == "leaf" && existing(productModalProduct.product)) {
      return customisationSelections.map(pmp => {
        if (pmp.product.key == productModalProduct.product.key)
          return productModalProduct;
        else
          return pmp;
        });
    } else {
      return customisationSelections.concat(productModalProduct);
    }
  }, [customisationSelections]);

  const customisationOptions = selectionGroup.products.map(opt => {
    const existingSelectionProducts = productModalSelectionGroup.productSelections.filter(pmp => {
      return (pmp.product.key === opt.key);
    });

    const removeSelection = (pmp : ProductModalProduct) => {
      setCustomisationSelection(
        updateCustomisationSelections(
          pmp.copyWith({ quantity: 0 })
        )
      )
    };

    const existingSelections = existingSelectionProducts.map(pmp => {
      return pmp.product.kind == "branch" && <SelectionGroupExistingSelection key={pmp.product.key} productModalProduct={pmp} removeSelection={removeSelection}/>;
    });

    const onChangeQuantity = (updatedQuantity: number) => {
      setCustomisationSelection(
        updateCustomisationSelections(
          ProductModalProduct.fromProduct(opt).copyWith({ quantity: updatedQuantity })
        )
      );
    };

    const quantity = customisationSelections
      .filter(ps => ps.product.key == opt.key)
      .reduce((accl, ps) => accl + ps.quantity, 0);
    const maxQuantity = customisationSelections
      .find(ps => ps.product.key == opt.key)
      ?.product?.maxQuantity ?? Number.POSITIVE_INFINITY;

    const remainingSelections = (selectionGroup.maxSelections ?? Number.POSITIVE_INFINITY) - currentTotalSelections;
    const remainingProductCopies = maxQuantity - quantity;

    const onCustomise = async () => {
      addCustomisationModal(
        ProductModalProduct.fromProduct(opt),
        selectionGroup.title,
        async (customisedProduct: ProductModalProduct) => {
          const quantisedProduct = updateCustomisationSelections(customisedProduct);
          setCustomisationSelection(quantisedProduct);
        },
        async (reason: string) => {
          if (reason !== "User didn't make a selection") throw reason;
        },
        remainingSelections,
      );
    }

    const elevatedAddControl = (() => {
      if (opt.kind != "branch" || remainingSelections == 0 || remainingProductCopies == 0 || quantity == 0) {
        return null;
      }

      return (
        <div className="line-item">
          <button
            className="button is-full-width"
            role="button"
            aria-label="Customise"
            onClick={onCustomise}
          >
            <StreamlineIcon href={addIconUrl} />
            {`Add another`}
          </button>
        </div>
      )
    })();

    return (
      <React.Fragment key={opt.key}>
        <SelectionGroupLineItem
          key={opt.key}
          selectionGroup={selectionGroup}
          product={opt}
          onChangeQuantity={onChangeQuantity}
          quantity={quantity}
          onCustomise={onCustomise}
          remainingQuantityAllowed={remainingSelections}
          disabledButtons={disabledButtons}
          includeThumbnailSpacing={includeThumbnailSpacing}
        />

        {existingSelections}
        {elevatedAddControl}
      </React.Fragment>
    );
  });

  return (
    <>
      <div className={classes}>
        <div className="label space-mr-xs flex-grow-9999">{selectionGroup.title}</div>
        <div className="is-flexbox align-items-center flex-grow-1">
          <div className="is-tiny-text is-aligned-left">{quantityHelperText}</div>
          {requiredBadge()}
          {minReachedBadge()}
          {maxReachedBadge()}
        </div>
      </div>

      <section className="container">
        {customisationOptions}
      </section>
    </>
  );
}

const SelectionGroupExistingSelection = ({
  productModalProduct,
  removeSelection
}: {
  productModalProduct: ProductModalProduct;
  removeSelection: (key : ProductModalProduct) => void;
}): React.JSX.Element => {
  const selectedProductPrice = productModalProduct.selectionGroups.reduce((accl, ci) => accl + ci.subtotal(), 0);
  const selectedSummary = `${productModalProduct.product.title}${priceAdjustmentLabel(selectedProductPrice)}`;

  return (
    <div className="line-item is-secondary">
      {selectedSummary}
      <button className="button is-icon-only" type="button" aria-label="Remove" onClick={() => removeSelection(productModalProduct)}>
        <StreamlineIcon href={bin2IconUrl} />
      </button>
    </div>
  );
}

const RequiredSingleChoice = ({
  selectionGroup,
  customisationSelections,
  setCustomisationSelection,
  defaultExpanded,
  addCustomisationModal,
  disabledButtons,
  includeThumbnailSpacing,
}: {
  selectionGroup: SelectionGroup;
  customisationSelections: ProductModalProduct[];
  setCustomisationSelection: (products: ProductModalProduct[]) => void;
  defaultExpanded: boolean;
  addCustomisationModal: (productModalProduct: ProductModalProduct, label: string, onConfirm: (customised: ProductModalProduct) => Promise<void>, onCancel: (reason: string) => Promise<void>, remainingQuantityAllowed?: number) => void;
  disabledButtons: boolean;
  includeThumbnailSpacing: boolean;
}): React.JSX.Element => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const customisationOptions = selectionGroup.products.map(opt => {
    const quantity = customisationSelections.find(ps => ps.product.key == opt.key) ? 1 : 0;
    const onCustomise = async () => {
      if (opt.kind == "branch") {
        const existingSelection = customisationSelections.find((element) => {
          return element.product.key == opt.key
        });
        addCustomisationModal(
          existingSelection || ProductModalProduct.fromProduct(opt),
          selectionGroup.title,
          async (customisedProduct) => {
            setCustomisationSelection([customisedProduct]);
          },
          async (reason) => {
            if (reason !== "User didn't make a selection") throw reason;
          },
          selectionGroup.maxSelections,
        );
      } else {
        setCustomisationSelection([ProductModalProduct.fromProduct(opt)]);
      }

      setExpanded(false);
    }

    return (
      <SelectionGroupLineItem
        key={`${selectionGroup.key}-${opt.key}`}
        selectionGroup={selectionGroup}
        product={opt}
        onChangeQuantity={onCustomise}
        quantity={quantity}
        onCustomise={onCustomise}
        disabledButtons={disabledButtons}
        includeThumbnailSpacing={includeThumbnailSpacing}
      />
    );
  });

  const requiredExpando = selectionGroup.isRequired() ?
    <div className="choice-expander">
      <span className="required-label">Required</span>
      <StreamlineIcon href={arrowDown1IconUrl} />
    </div> : <></>;

  const selectedProductTitle = customisationSelections.map(ci => ci.product.title)?.join(", ");
  const selectedProductPrice = customisationSelections.reduce((accl, ci) => accl + ci.subtotal(), 0);

  const selectedSummary = customisationSelections.length > 0 ? `${selectedProductTitle}${priceAdjustmentLabel(selectedProductPrice)}` : undefined;
  const summaryValue = selectionGroup.isRequired() &&
    <div className="choice-value">{selectedSummary ?? "-"}</div>;

  const divider = (
    <summary className="divider has-content">
      <div className="choice-summary">
        <div className="choice-label">{selectionGroup.title}</div>
        {summaryValue}
      </div>

      {requiredExpando}
    </summary>
  );

  const section = (
    <section className="container">
      <fieldset>
        <legend>{selectionGroup.title}</legend>

        {customisationOptions}
      </fieldset>
    </section>
  );

  if (selectionGroup.isRequired()) {
    return (
      <details
        className={classNames({
          "required-choice": true,
          "is-valid": !!selectedProductTitle
        })}
        open={expanded}
        onToggle={e => setExpanded((e.currentTarget as HTMLDetailsElement).open)}
      >
        {divider}
        {section}
      </details>
    );
  } else {
    return <>
      {divider}
      {section}
    </>;
  }
}

const ProductImageElement = ({ imageUrls = [] }: { imageUrls: string[] }) : React.JSX.Element => {
  const storefrontConfig = useStorefrontConfig();
  const primaryImageUrl: string | undefined = imageUrls[0];

  if (primaryImageUrl) {
    const variant = storefrontConfig.storefrontExperience.useGridProductDisplay ? '360x360' : '480x360';

    const showNavigationControls = imageUrls.length > 1;

    return (
      <div className={`product-image-large has-background-image ${storefrontConfig.storefrontExperience.useGridProductDisplay ? 'is-square' : ''}`} style={dynamicBackgroundProperties(primaryImageUrl, variant)} data-controller="indicator-scroll">
        <div className="images-container" data-indicator-scroll-target="content">
          {showNavigationControls ?
            <>
              <div className="navigation-controls is-visible-above-desktop">
                <button
                  className="button is-icon-only has-large-icon is-raised has-plain-icon is-tiny"
                  aria-label="Previous"
                  data-indicator-scroll-index-param="prev"
                  data-indicator-scroll-target="prevButton"
                  data-action="click->indicator-scroll#onIndicatorClick"
                >
                  <StreamlineIcon href={arrowLeftIconUrl} />
                </button>
                <button
                  className="button is-icon-only has-large-icon is-raised has-plain-icon is-tiny"
                  aria-label="Next"
                  data-indicator-scroll-index-param="next"
                  data-indicator-scroll-target="nextButton"
                  data-action="click->indicator-scroll#onIndicatorClick"
                >
                  <StreamlineIcon href={arrowRightIconUrl} />
                </button>
              </div>


              <div className="pagination-indicator">
                {imageUrls.map((imageUrl, index) =>
                  <div key={imageUrl} className={`indicator-dot ${index === 0 ? 'is-active' : ''}`}
                       data-indicator-scroll-target="indicator"
                       data-indicator-scroll-index-param={index} data-action="click->indicator-scroll#onIndicatorClick">
                    <div></div>
                  </div>
                )}
              </div>
            </> :
            null}

          {imageUrls.map((imageUrl, index) => <div key={imageUrl} className="image-container" style={dynamicBackgroundProperties(imageUrl, variant)} data-indicator-scroll-target="item" data-index={index}></div>)}
        </div>
      </div>
    )
  } else {
    return (
      <div className="product-image-large"></div>
    )
  }
}

const ProductDetails = ({ product, showBreadcrumbs }: { product: Product; showBreadcrumbs: boolean }): React.JSX.Element => {
  const { getImageUrls } = useStorefrontHelper();

  const storefrontHelper = useStorefrontHelper();
  const { catalog } = storefrontHelper;
  const { usesSubcategoryDisplay } = useStorefrontConfig();

  const [_navigationState, updateNavigation] = useNavigationState();

  const formatter = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  });

  const priceDisplay = product.price ? formatter.format(product.price / 100.0) : "";
  const [showToggle, setToggleVisibility] = useState(false);

  const handleToggleVisibility = useCallback(() => {
    const description : HTMLElement | null = document.querySelector(".description.text-overflow-toggle");
    const paragraph : HTMLElement | null = document.querySelector(".description.text-overflow-toggle > p");

    if (paragraph && description) {
       setToggleVisibility(paragraph.scrollHeight > paragraph.clientHeight);
    }
  }, []);

  const onBreadcrumbClick = useCallback((catalogComponent: CatalogComponent) => {
    updateNavigation(SelectBreadcrumb.action(catalogComponent.fullKeyPath()))
  }, [updateNavigation]);

  const onAllProductsClick = useCallback(() => {
    updateNavigation(SelectBreadcrumb.action([]))
  }, [updateNavigation]);

  const breadcrumbNav = useMemo(() => {
    const breadcrumbCategories = catalog.findCatalogComponentsFromKeyPath(product.fullKeyPath()).slice(1, -1)
    const breadcrumbs: Breadcrumb[] = breadcrumbCategories.map(cat => {
      return {
        key: cat.key,
        title: cat.title,
        onBreadcrumbClick: () => onBreadcrumbClick(cat)
      };
    })

    return (
      <div className="divider has-content choice-strip">
        <Breadcrumbs breadcrumbs={breadcrumbs} onAllClick={onAllProductsClick} />
      </div>
    );
  }, [catalog, onBreadcrumbClick, product, onAllProductsClick]);

  useEffect(() => {
    handleToggleVisibility();

    window.addEventListener('resize', handleToggleVisibility);

    return () => {
      window.removeEventListener('resize', handleToggleVisibility);
    };
  }, [handleToggleVisibility]);

  return (
    <>
      <ProductImageElement imageUrls={getImageUrls(product)} />
      {showBreadcrumbs && usesSubcategoryDisplay ? breadcrumbNav : null}
      <section>
        <div className="container">
          <div className="product-details">
            <div className="title">{product.title}</div>
            <div className="price">
              <div>{priceDisplay}</div>
              {product.unitPriceLabel ? <div className="is-tiny-text is-muted-text">{product.unitPriceLabel}</div> : <></>}
            </div>
            <div className="description text-overflow-toggle">
              {showToggle && <input name="expand_product_description" id="expand_product_description" type="checkbox" />}

              {product.description ? <p>{product.description}</p> : null}

              {showToggle &&
                <label htmlFor="expand_product_description">
                  <StreamlineIcon href={moveExpandVerticalIconUrl} />
                  <span>Full description</span>
                </label>
              }
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const ProductSelectionGroups = ({
  product,
  modalState,
  setSelectionGroupSelections,
  addCustomisationModal,
  disabledButtons,
}: {
  product: Product;
  modalState: ProductModalProduct;
  setSelectionGroupSelections: (selectionGroupKey: string, selectedProductKeys: ProductModalProduct[]) => void;
  addCustomisationModal: (productModalProduct: ProductModalProduct, label: string, onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>, onCancel: (reason: string) => Promise<void>) => void;
  disabledButtons: boolean;
}): React.JSX.Element => {
  const { isOrderable } = useStorefrontHelper();

  if (product.kind == "branch") {
    const customisations = product.selectionGroups.map(group => {
      const productSelections = group.products
        .filter((product) => product.defaultQuantity )
        .map((product) => ProductModalProduct.fromProduct(product));
      const newSelectionGroup = () => new ProductModalSelectionGroup(group, productSelections);

      let customisationSelection = modalState.selectionGroups.find(sg => sg.selectionGroup.key == group.key) ?? newSelectionGroup();
      customisationSelection = customisationSelection.copyWith({ productSelections: customisationSelection.productSelections.filter((productModalProduct) => isOrderable(productModalProduct.product)) });

      const setCustomisationSelection = (products: ProductModalProduct[]): void => {
        setSelectionGroupSelections(group.key, products);
      };

      const includeThumbnailSpacing = group.products.some((product) => product.images.length > 0);

      if (group.minSelections === 1 && group.maxSelections === 1)
        return (
          <RequiredSingleChoice
            selectionGroup={group}
            key={group.key}
            customisationSelections={customisationSelection?.productSelections || []}
            setCustomisationSelection={setCustomisationSelection}
            defaultExpanded={product.requiredSelectionGroups().length === 1}
            addCustomisationModal={addCustomisationModal}
            disabledButtons={disabledButtons}
            includeThumbnailSpacing={includeThumbnailSpacing}
          />
        );
      else
        return (
          <ProductCustomisation
            key={group.key}
            productModalSelectionGroup={customisationSelection}
            setCustomisationSelection={setCustomisationSelection}
            addCustomisationModal={addCustomisationModal}
            disabledButtons={disabledButtons}
            includeThumbnailSpacing={includeThumbnailSpacing}
          />
        );
    });

    return (<>{customisations}</>);
  }

  return <></>;
}

const ProductMealPreferences = ({ mealPreferences, setMealPreferences }: { product: Product; mealPreferences: string; setMealPreferences: (newMealPreferences: string) => void }): React.JSX.Element => {
  return (
    <>
      <div className="divider has-content">
        <div className="label">Meal preferences</div>
        <span className="is-tiny-text is-aligned-right"></span>
      </div>

      <section>
        <div className="container">
          <div className="textarea has-hidden-label">
            <label htmlFor="meal_preferences">Meal preferences</label>
            <textarea
              name="meal_preferences"
              id="meal_preferences"
              data-testid="meal_preferences"
              placeholder="E.g. ‘Easy on the salt’ or ‘No gherkins’"
              onChange={e => setMealPreferences((e.currentTarget as HTMLTextAreaElement).value)}
              value={mealPreferences}>
            </textarea>
          </div>
        </div>
      </section>
    </>
  );
}

const ProductQuantity = ({
  quantity, setQuantity, maxQuantity, disabledButtons
}: {
  quantity: number;
  setQuantity: (newQuantity: number) => void;
  maxQuantity: number;
  disabledButtons: boolean;
}): React.JSX.Element => {
  const decrementQuantity = useCallback(() => {
    setQuantity(Math.max(1, quantity - 1));
  }, [quantity, setQuantity]);

  const incrementQuantity = useCallback(() => {
    setQuantity(quantity + 1);
  }, [quantity, setQuantity]);

  const onChangeQuantity = useCallback((v: number) => {
    setQuantity(Math.min(v, maxQuantity));
  }, [setQuantity, maxQuantity]);

  return (
    <>
      <div className="divider has-content">
        <div className="label">Quantity</div>
        <span className="is-tiny-text is-aligned-right"></span>
      </div>

      <section>
        <div className="container">
          <QuantityInput
            onDecrement={decrementQuantity}
            onIncrement={incrementQuantity}
            onChangeQuantity={onChangeQuantity}
            quantity={quantity}
            canIncrement={quantity < maxQuantity}
            disabledButtons={disabledButtons}
          />
        </div>
      </section>
    </>
  );
}

type SelectionGroupLineItemProps = {
  selectionGroup: SelectionGroup;
  product: Product;
  quantity: number;
  onChangeQuantity: (newQuantity: number) => void;
  onCustomise: () => void;
  remainingQuantityAllowed?: number;
  disabledButtons: boolean;
  includeThumbnailSpacing: boolean;
};

const SelectionGroupLineItem = ({
  selectionGroup,
  product,
  quantity,
  onChangeQuantity,
  onCustomise,
  remainingQuantityAllowed,
  disabledButtons,
  includeThumbnailSpacing
}: SelectionGroupLineItemProps) : React.JSX.Element => {
  const { isOrderable, getPrimaryImageUrl } = useStorefrontHelper();
  const { hideOutOfStockItems } = useStorefrontConfig();
  const isUnavailable = !isOrderable(product);

  const onCustomiseWrapper = useCallback(() => {
    if (!isUnavailable) {
      onCustomise();
    }
  }, [onCustomise, isUnavailable]);

  const onChangeQuantityWrapper = useCallback((n: number) => {
    if (!isUnavailable) {
      onChangeQuantity(n);
    }
  }, [onChangeQuantity, isUnavailable]);

  const minQuantity = 0;
  const maxProductQuantity = product.maxQuantity && product.maxQuantity > 0 ? product.maxQuantity : Number.POSITIVE_INFINITY;
  const maxQuantity = Math.min(maxProductQuantity, quantity + (remainingQuantityAllowed ?? Number.POSITIVE_INFINITY));

  const onDecrement = useCallback(() => {
    onChangeQuantityWrapper(Math.max(minQuantity, quantity - 1));
  }, [onChangeQuantityWrapper, minQuantity, quantity]);

  const onIncrement = useCallback(() => {
    onChangeQuantityWrapper(Math.min(maxQuantity, quantity + 1));
  }, [onChangeQuantityWrapper, maxQuantity, quantity]);

  const isToggleInsteadOfQuantity = (selectionGroup.maxSelections && selectionGroup.maxSelections === 1) || (product.maxQuantity === 1);
  const isCustomisable = product.kind == "branch";
  const suppressControls = isCustomisable && quantity > 0 && !isToggleInsteadOfQuantity;

  const quantitySelect = isCustomisable || isToggleInsteadOfQuantity || suppressControls ? <></> : <QuantityInput
    onDecrement={onDecrement}
    onIncrement={onIncrement}
    onChangeQuantity={onChangeQuantityWrapper}
    quantity={quantity}
    canDecrement={quantity > minQuantity}
    canIncrement={quantity < maxQuantity}
    disabledButtons={disabledButtons || isUnavailable}
  />;

  const addIcon = <StreamlineIcon href={addIconUrl} />

  let onClickAction = function() {
    // Do nothing
  };

  if (!isToggleInsteadOfQuantity && isCustomisable && !suppressControls){
    onClickAction = onCustomiseWrapper;
  } else if (!isToggleInsteadOfQuantity && !isCustomisable && quantity < 1){
    onClickAction = onIncrement;
  }

  const thumbnailImage = includeThumbnailSpacing && <div
    className="thumbnail-image"
    data-lazy-images-target="image"
    data-lazy-images-imagery-variant="50x50"
    data-lazy-images-imagery-path-value={getPrimaryImageUrl(product)}
  ></div>;

  const customiseChevron = !isToggleInsteadOfQuantity && isCustomisable && !suppressControls ?
    <button className="button is-icon-only" type="button" aria-label="Customise" onClick={onClickAction} disabled={disabledButtons}>
      {addIcon}
    </button> : null;

  const quantityBadge = suppressControls ? <span className="badge is-quantity">{quantity}</span> : null

  const children = <>
    {!isToggleInsteadOfQuantity && thumbnailImage}
    <ChoiceLabel
      option={product}
      selectionGroup={selectionGroup}
      showChevron={isToggleInsteadOfQuantity && isCustomisable}
      onClick={onClickAction}
      disabledButtons={disabledButtons}
    >
      {isToggleInsteadOfQuantity && thumbnailImage}
    </ChoiceLabel>
    {quantitySelect}
    {customiseChevron}
    {quantityBadge}
  </>;

  const classes = classNames({
    "line-item": true,
    "is-disabled": disabledButtons || isUnavailable,
  });

  if (isUnavailable && hideOutOfStockItems) {
    return (<></>);
  } else {
    return (
      <div className={classes}>
        <SelectionGroupFormControl
          selectionGroup={selectionGroup}
          product={product}
          quantity={quantity}
          onChangeQuantity={onChangeQuantityWrapper}
          onCustomise={onCustomiseWrapper}
          canIncrement={quantity < maxQuantity}
          disabledButtons={disabledButtons}
        >
          {children}
        </SelectionGroupFormControl>
      </div>
    );
  }
}

export { ProductModalCard };
